/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { getCartState } from '@msdyn365-commerce/global-state';
import { CartLine, QueryResultSettings } from '@msdyn365-commerce/retail-proxy';
import { addCartLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import {
    ArrayExtensions,
    getPaginatedOrderTemplateLinesAction,
    GetPaginatedOrderTemplateLinesInput
} from '@msdyn365-commerce-modules/retail-actions';
import { _getProductDetails, _getProductInventory, IHSOProductInventory, getProductDetails } from '../components/common/hso-helpers';
import {
    SimpleProduct,
    ProductWarehouseInventoryInformation,
    AttributeValueBase
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
/**
 *  Input class for the AddOrderTemplateToCart data action.
 */
export class AddOrderTemplateToCartInput implements IActionInput {
    public readonly orderTemplateId: string;
    public readonly productDetails?: IHSOProductInventory[];

    constructor(orderTemplateId: string, productDetails?: IHSOProductInventory[]) {
        this.orderTemplateId = orderTemplateId;
        this.productDetails = productDetails;
    }

    public getCacheKey = () => 'AddOrderTemplateToCartInput';

    public getCacheObjectType = () => 'AddOrderTemplateToCartInput';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the AddOrderTemplateToCart method.
 * @param {ICreateActionContext} inputData The input data passed to the createInput method.
 * @param {string} orderTemplateId The id of template.
 */
export const addOrderTemplateToCartInput = (inputData: ICreateActionContext, orderTemplateId: string): AddOrderTemplateToCartInput => {
    const { requestContext } = inputData;
    if (!requestContext.user.isAuthenticated) {
        throw new Error('Unable to create order template. User is not authenticated.');
    }
    return new AddOrderTemplateToCartInput(orderTemplateId);
};

export async function validateReplacementProduct(
    input: AddOrderTemplateToCartInput,
    context: IActionContext,
    enableAlternateProductRedirect?: boolean
): Promise<boolean> {
    let hasRedirectProduct = false;
    const chunkCount = 1000;
    const queryResultSettings: QueryResultSettings = {
        Paging: {
            Top: chunkCount,
            Skip: 0
        }
    };
    const getPaginatedOrderTemplateLinesInput = new GetPaginatedOrderTemplateLinesInput(input.orderTemplateId, queryResultSettings);
    let orderTemplateLines = await getPaginatedOrderTemplateLinesAction(getPaginatedOrderTemplateLinesInput, context);

    let orderTemplateDetail: IHSOProductInventory[] = [];
    if (orderTemplateLines) {
        const products = orderTemplateLines.lines.map(element => {
            return element.fullProduct?.ProductDetails;
        });
        if (products) {
            const calculateProducts = products as SimpleProduct[];
            const productDetailsPromise = getProductDetails(calculateProducts, context.requestContext.apiSettings.channelId, context);
            orderTemplateDetail = await productDetailsPromise;
        }
    }

    for (let skip = chunkCount; orderTemplateLines.lines.length > 0; skip += chunkCount) {
        let cartLines: CartLine[] = orderTemplateLines.lines.map((productLine, index, array) => {
            let cartLine: CartLine = {
                ItemId: productLine.fullProduct?.ProductDetails.ItemId,
                ProductId: productLine.productListLine.ProductId,
                Quantity: productLine.productListLine.Quantity,
                TrackingId: '',
                UnitOfMeasureSymbol: productLine.productListLine.UnitOfMeasure
            };
            let selectedProduct: SimpleProduct | null = null;
            let selectedProductInventory: ProductWarehouseInventoryInformation | null = null;

            for (const productDetail of orderTemplateDetail!) {
                if (productDetail.productDetails?.RecordId === productLine.fullProduct?.ProductDetails.RecordId) {
                    selectedProduct = productDetail.productDetails;
                    selectedProductInventory = productDetail.productsInventory;
                }
            }

            if (selectedProduct && enableAlternateProductRedirect) {
                const useAltItem: number =
                    (selectedProduct &&
                        selectedProduct.ExtensionProperties &&
                        selectedProduct.ExtensionProperties?.some(ep => {
                            return ep.Key?.startsWith('USEALTITEMID');
                        }) &&
                        ArrayExtensions.firstOrDefault(
                            selectedProduct.ExtensionProperties?.filter(ep => {
                                return ep.Key?.startsWith('USEALTITEMID');
                            })
                        )?.Value?.IntegerValue) ||
                    0;

                const altItem: number =
                    (selectedProduct &&
                        selectedProduct.ExtensionProperties &&
                        selectedProduct.ExtensionProperties?.some(ep => {
                            return ep.Key?.startsWith('ALTPRODUCT');
                        }) &&
                        ArrayExtensions.firstOrDefault(
                            selectedProduct.ExtensionProperties?.filter(ep => {
                                return ep.Key?.startsWith('ALTPRODUCT');
                            })
                        )?.Value?.LongValue) ||
                    0;
                const altItemId: string =
                    (selectedProduct &&
                        selectedProduct.ExtensionProperties &&
                        selectedProduct.ExtensionProperties?.some(ep => {
                            return ep.Key?.startsWith('ALTITEMID');
                        }) &&
                        ArrayExtensions.firstOrDefault(
                            selectedProduct.ExtensionProperties?.filter(ep => {
                                return ep.Key?.startsWith('ALTITEMID');
                            })
                        )?.Value?.StringValue) ||
                    '';
                if (useAltItem > -1 && altItem > 0 && altItemId) {
                    let productAvailability = selectedProductInventory!.AggregatedProductInventoryAvailabilities![0]
                        .PhysicalAvailableQuantity;
                    if (useAltItem === 1 && productAvailability! > 0) {
                        hasRedirectProduct = true;
                        cartLine = {
                            ItemId: altItemId,
                            ProductId: altItem,
                            Quantity: productLine.productListLine.Quantity,
                            TrackingId: '',
                            UnitOfMeasureSymbol: productLine.productListLine.UnitOfMeasure
                        };
                    }
                    if (useAltItem === 2) {
                        hasRedirectProduct = true;
                        cartLine = {
                            ItemId: altItemId,
                            ProductId: altItem,
                            Quantity: productLine.productListLine.Quantity,
                            TrackingId: '',
                            UnitOfMeasureSymbol: productLine.productListLine.UnitOfMeasure
                        };
                    }
                }
            }
            return cartLine;
        });

        if (cartLines.length > 0) {
            console.log('Added products to list');
        }
    }
    return Promise.resolve(hasRedirectProduct);
}

/**
 * The action method for the AddOrderTemplateToCart data action.
 * @param {AddOrderTemplateToCartInput} input The action input.
 * @param {IActionContext} ctx The action context.
 * @param context
 */
export async function addOrderTemplateToCartAction(
    input: AddOrderTemplateToCartInput,
    context: IActionContext,
    enableAlternateProductRedirect?: boolean
): Promise<CartLine[]> {
    const cartState = await getCartState(context);
    const chunkCount = 1000;
    const queryResultSettings: QueryResultSettings = {
        Paging: {
            Top: chunkCount,
            Skip: 0
        }
    };
    let hasProductRedirect = false;
    const getPaginatedOrderTemplateLinesInput = new GetPaginatedOrderTemplateLinesInput(input.orderTemplateId, queryResultSettings);
    let orderTemplateLines = await getPaginatedOrderTemplateLinesAction(getPaginatedOrderTemplateLinesInput, context);
    let addedCartLines: CartLine[] = [];

    let orderTemplateDetail: IHSOProductInventory[] = [];
    if (orderTemplateLines) {
        const products = orderTemplateLines.lines.map(element => {
            return element.fullProduct?.ProductDetails;
        });
        if (products) {
            const calculateProducts = products as SimpleProduct[];
            const productDetailsPromise = getProductDetails(calculateProducts, context.requestContext.apiSettings.channelId, context);
            orderTemplateDetail = await productDetailsPromise;
        }
    }

    for (let skip = chunkCount; orderTemplateLines.lines.length > 0; skip += chunkCount) {
        let cartLines: CartLine[] = orderTemplateLines.lines.map((productLine, index, array) => {
            let cartLine: CartLine = {
                ItemId: productLine.fullProduct?.ProductDetails.ItemId,
                ProductId: productLine.productListLine.ProductId,
                Quantity: productLine.productListLine.Quantity,
                TrackingId: '',
                UnitOfMeasureSymbol: productLine.productListLine.UnitOfMeasure
            };
            let selectedProduct: SimpleProduct | null = null;
            let selectedProductInventory: ProductWarehouseInventoryInformation | null = null;

            for (const productDetail of orderTemplateDetail!) {
                if (productDetail.productDetails?.RecordId === productLine.fullProduct?.ProductDetails.RecordId) {
                    selectedProduct = productDetail.productDetails;
                    selectedProductInventory = productDetail.productsInventory;
                }
            }

            if (selectedProduct && enableAlternateProductRedirect) {
                const useAltItem: number =
                    (selectedProduct &&
                        selectedProduct.ExtensionProperties &&
                        selectedProduct.ExtensionProperties?.some(ep => {
                            return ep.Key?.startsWith('USEALTITEMID');
                        }) &&
                        ArrayExtensions.firstOrDefault(
                            selectedProduct.ExtensionProperties?.filter(ep => {
                                return ep.Key?.startsWith('USEALTITEMID');
                            })
                        )?.Value?.IntegerValue) ||
                    0;

                const altItem: number =
                    (selectedProduct &&
                        selectedProduct.ExtensionProperties &&
                        selectedProduct.ExtensionProperties?.some(ep => {
                            return ep.Key?.startsWith('ALTPRODUCT');
                        }) &&
                        ArrayExtensions.firstOrDefault(
                            selectedProduct.ExtensionProperties?.filter(ep => {
                                return ep.Key?.startsWith('ALTPRODUCT');
                            })
                        )?.Value?.LongValue) ||
                    0;
                const altItemId: string =
                    (selectedProduct &&
                        selectedProduct.ExtensionProperties &&
                        selectedProduct.ExtensionProperties?.some(ep => {
                            return ep.Key?.startsWith('ALTITEMID');
                        }) &&
                        ArrayExtensions.firstOrDefault(
                            selectedProduct.ExtensionProperties?.filter(ep => {
                                return ep.Key?.startsWith('ALTITEMID');
                            })
                        )?.Value?.StringValue) ||
                    '';
                if (useAltItem > -1 && altItem > 0 && altItemId) {
                    let productAvailability = selectedProductInventory!.AggregatedProductInventoryAvailabilities![0]
                        .PhysicalAvailableQuantity;
                    if (useAltItem === 1 && productAvailability! > 0) {
                        hasProductRedirect = true;
                        cartLine = {
                            ItemId: altItemId,
                            ProductId: altItem,
                            Quantity: productLine.productListLine.Quantity,
                            TrackingId: '',
                            UnitOfMeasureSymbol: productLine.productListLine.UnitOfMeasure
                        };
                    }
                    if (useAltItem === 2) {
                        hasProductRedirect = true;
                        cartLine = {
                            ItemId: altItemId,
                            ProductId: altItem,
                            Quantity: productLine.productListLine.Quantity,
                            TrackingId: '',
                            UnitOfMeasureSymbol: productLine.productListLine.UnitOfMeasure
                        };
                    }
                }
            }
            return cartLine;
        });

        const cartFilterUndefined = cartLines.filter(element => {
            return element.ItemId !== undefined;
        });

        await addCartLinesAsync({ callerContext: context }, cartState.cart.Id, cartFilterUndefined, cartState.cart.Version).catch(error => {
            context.telemetry.trace(error.data?.ValidationResults);
            context.telemetry.trace('Unable to add order template to cart.');
            throw error.data?.ValidationResults;
        });
        addedCartLines = addedCartLines.concat(cartLines);
        getPaginatedOrderTemplateLinesInput.queryResultSettings.Paging!.Skip = skip;
        orderTemplateLines = await getPaginatedOrderTemplateLinesAction(getPaginatedOrderTemplateLinesInput, context);
    }

    if (hasProductRedirect) {
        let attributeObj: AttributeValueBase = {
            Name: 'HSOREPLACE',
            ExtensionProperties: [
                {
                    Key: 'HSOREPLACE',
                    Value: {
                        BooleanValue: hasProductRedirect
                    }
                }
            ]
        };
        attributeObj.Name = 'HSOREPLACE';
        if (addedCartLines[0].AttributeValues) {
            addedCartLines[0].AttributeValues?.push(attributeObj);
        } else {
            addedCartLines[0].AttributeValues = [attributeObj];
        }
    }

    await cartState.refreshCart({});
    return addedCartLines;
}

/**
 * The AddOrderTemplateToCart Data Action.
 * Returns created order template.
 */
export const addOrderTemplateToCartActionDataAction = createObservableDataAction<CartLine[]>({
    id: '@msdyn365-commerce-modules/retail-actions/order-templates/add-order-template-to-cart',
    action: <IAction<CartLine[]>>addOrderTemplateToCartAction,
    input: addOrderTemplateToCartInput
});
export default addOrderTemplateToCartActionDataAction;
